import React from 'react';
import { Button, Checkbox, Typography } from '@scale/scaleui';
import { useToggle } from 'react-use-v13';
import { NLPLintCondition } from '@scale/shared/task_lint/linters/categorization';
import ConditionBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ConditionBuilder';
import JSONInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/JSONInputBuilder';
import BuilderHint from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderHint';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';

const ConditionsBuilder: React.FC<{
  name: string;
  updateProperty: (value: any) => void;
  existingValue?: NLPLintCondition[];
  required?: boolean;
  hint?: string;
  fields?: Array<TextCollectionField>;
  taskTemplateParams?: Record<string, any>;
}> = ({ name, updateProperty, existingValue, required, hint, fields, taskTemplateParams }) => {
  const conditions = existingValue || [];
  const [useOverride, setUseOverride] = useToggle(false);

  const setConditions = (index: number, newCondition: NLPLintCondition) => {
    const newConditions = [...conditions];
    newConditions[index] = newCondition;
    updateProperty(newConditions);
  };
  const removeConditions = (index: number) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    if (newConditions.length === 0) {
      // Validator doesn't like an empty conditions array
      updateProperty(undefined);
    } else {
      updateProperty(newConditions);
    }
  };
  const addCondition = () => {
    const newFields = [...conditions];
    newFields.push({});
    updateProperty(newFields);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row">
        <Typography.Paragraph2>{name}</Typography.Paragraph2>
        {hint && <BuilderHint name={name} hint={hint} />}
      </div>
      <Checkbox
        checked={useOverride}
        label="Use JSON Override"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setUseOverride(e.target.checked);
        }}
      />
      {useOverride ? (
        <JSONInputBuilder
          name="Conditions JSON"
          updateProperty={updateProperty}
          existingValue={existingValue}
          required={required}
        />
      ) : (
        <div className="flex flex-col gap-2 max-w-lg">
          {conditions.map((condition, i) => (
            <div key={i}>
              <ConditionBuilder
                index={i}
                condition={condition}
                updateCondition={(newCondition: NLPLintCondition) => setConditions(i, newCondition)}
                deleteCondition={() => removeConditions(i)}
                fields={fields}
                taskTemplateParams={taskTemplateParams}
              />
              {i !== conditions.length - 1 && (
                <div className="mt-2">
                  <Typography.Paragraph1>OR</Typography.Paragraph1>
                </div>
              )}
            </div>
          ))}
          <div>
            <Button onClick={() => addCondition()}>Add condition</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionsBuilder;
